import { Controller } from "@hotwired/stimulus";

// 点検一覧画面の検索フォーム用コントローラー。
// inspections_report_form_controller.ts（帳票出力画面用）とカスケード・検索ロジックはほぼ同一だが、
// 期間の選択方式が異なる（こちらは日付範囲、あちらは月単位チェックボックス）ため、
// 将来の画面固有の拡張性を考慮してあえてファイルを分けている。

// カスケードと検索の可視性を分離管理するためのdata属性キー
const CASCADE_HIDDEN = "cascadeHidden";
const SEARCH_HIDDEN = "searchHidden";

export default class extends Controller {
  static targets = [
    "startDate",
    "endDate",
    "siteItem",
    "nullSiteCheckbox",
    "siteCheckbox",
    "checklistCheckbox",
    "categoryGroup",
    "resourceGroup",
    "noSitesMessage",
    "noCategoriesMessage",
    "noResourcesMessage",
  ];

  declare readonly startDateTarget: HTMLInputElement;
  declare readonly endDateTarget: HTMLInputElement;
  declare readonly siteItemTargets: HTMLElement[];
  declare readonly nullSiteCheckboxTargets: HTMLInputElement[];
  declare readonly siteCheckboxTargets: HTMLInputElement[];
  declare readonly checklistCheckboxTargets: HTMLInputElement[];
  declare readonly categoryGroupTargets: HTMLElement[];
  declare readonly resourceGroupTargets: HTMLElement[];
  declare readonly noSitesMessageTargets: HTMLElement[];
  declare readonly noCategoriesMessageTargets: HTMLElement[];
  declare readonly noResourcesMessageTargets: HTMLElement[];

  connect(): void {
    this.applyCascadeFilters();
    this.restoreParentCheckboxes();
  }

  // フォーム送信前に、チェック状態に応じてパラメータ名を調整し、
  // 全OFFの場合はダミー値で0件を返すようにする
  beforeSubmit(): void {
    const includeNull = this.nullSiteCheckboxTargets.some((cb) => cb.checked);
    const hasCheckedSite = this.siteCheckboxTargets.some((cb) => cb.checked);

    if (includeNull) {
      // 「現場なし」ON: site_id_in_or_nullでNULL含む検索
      this.siteCheckboxTargets.forEach((cb) => {
        cb.name = "q[site_id_in_or_null][]";
      });
      // サイトが1つもチェックされていなくてもNULLだけで検索されるようダミー送信
      if (!hasCheckedSite) {
        this.ensureEmptyFilter("q[site_id_in_or_null][]", false);
      } else {
        this.removeEmptyFilter("q[site_id_in_or_null][]");
      }
      this.removeEmptyFilter("q[site_id_in][]");
    } else {
      this.siteCheckboxTargets.forEach((cb) => {
        cb.name = "q[site_id_in][]";
      });
      this.ensureEmptyFilter("q[site_id_in][]", hasCheckedSite);
      this.removeEmptyFilter("q[site_id_in_or_null][]");
    }

    this.ensureEmptyFilter("q[check_list_id_in][]", this.hasCheckedChecklist());
    this.ensureEmptyFilter("q[resource_id_in][]", this.hasCheckedResource());

    // 検索後にURLを更新してF5リロードで状態を復元できるようにする
    this.updateUrlAfterSubmit();
  }

  private hasCheckedChecklist(): boolean {
    return this.checklistCheckboxTargets.some((cb) => cb.checked);
  }

  private hasCheckedResource(): boolean {
    return this.element.querySelectorAll<HTMLInputElement>("input[name='q[resource_id_in][]']:checked").length > 0;
  }

  // チェックが0件の場合、存在しないダミー値を送信して0件を返す
  private ensureEmptyFilter(name: string, hasChecked: boolean): void {
    const existingDummy = this.element.querySelector<HTMLInputElement>(`input[type='hidden'][name='${name}'][data-dummy]`);
    if (hasChecked) {
      existingDummy?.remove();
    } else if (!existingDummy) {
      const hidden = document.createElement("input");
      hidden.type = "hidden";
      hidden.name = name;
      hidden.value = "___none___";
      hidden.dataset.dummy = "true";
      this.element.appendChild(hidden);
    }
  }

  private removeEmptyFilter(name: string): void {
    this.element.querySelector<HTMLInputElement>(`input[type='hidden'][name='${name}'][data-dummy]`)?.remove();
  }

  // Turbo Frameでは送信後もURLが変わらないため、手動でURLを更新する
  private updateUrlAfterSubmit(): void {
    const formData = new FormData(this.element as HTMLFormElement);
    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key !== "commit" && key !== "utf8" && value !== "") {
        params.append(key, value as string);
      }
    });
    const newUrl = `${window.location.pathname}?${params.toString()}`;
    setTimeout(() => history.replaceState(null, "", newUrl), 0);
  }

  // URL復元時に子のチェック状態から親チェックボックスを再計算
  private restoreParentCheckboxes(): void {
    [...this.categoryGroupTargets, ...this.resourceGroupTargets].forEach((group) => {
      this.updateGroupParentCheckboxes(group);
    });
  }

  // --- テキスト検索フィルター ---

  filterBySite(event: Event): void {
    const text = this.getInputValue(event);
    this.applySearchFlatList(this.siteItemTargets, text);
    this.updateEmptyMessage(this.noSitesMessageTargets, this.siteItemTargets);
    this.applyCascadeForCategories();
    this.applyCascadeForResources();
  }

  filterByCategory(event: Event): void {
    const text = this.getInputValue(event);
    this.applySearchHierarchicalList(this.categoryGroupTargets, text);
    this.updateEmptyMessage(this.noCategoriesMessageTargets, this.categoryGroupTargets);
    this.applyCascadeForResources();
  }

  filterByResource(event: Event): void {
    const text = this.getInputValue(event);
    this.applySearchHierarchicalList(this.resourceGroupTargets, text);
    this.updateEmptyMessage(this.noResourcesMessageTargets, this.resourceGroupTargets);
  }

  // --- カスケード連動 ---

  onPeriodChange(): void {
    this.applyCascadeFilters();
  }

  onSiteChange(): void {
    this.applyCascadeForCategories();
    this.applyCascadeForResources();
  }

  onChecklistChange(): void {
    this.applyCascadeForResources();
  }

  // --- 親子チェックボックス連動 ---

  toggleGroupChildren(event: Event): void {
    const checkbox = event.target as HTMLInputElement;
    const group = checkbox.closest<HTMLElement>("[data-child-role]")!;
    group.querySelectorAll<HTMLElement>("[data-role]").forEach((item) => {
      if (this.isHidden(item)) return;
      const cb = item.querySelector<HTMLInputElement>("input[type='checkbox']");
      if (cb) cb.checked = checkbox.checked;
    });
  }

  updateGroupParent(event: Event): void {
    const checkbox = event.target as HTMLInputElement;
    let currentItem = checkbox.closest<HTMLElement>("[data-role]");
    while (currentItem) {
      const group = currentItem.parentElement?.closest<HTMLElement>("[data-child-role]");
      if (!group) break;
      const childRole = group.dataset.childRole!;
      const visibleCheckboxes = Array.from(group.querySelectorAll<HTMLElement>(`[data-role='${childRole}']`))
        .filter((el) => !this.isHidden(el))
        .map((el) => el.querySelector<HTMLInputElement>("input[type='checkbox']"))
        .filter((cb): cb is HTMLInputElement => cb !== null);
      const allChecked = visibleCheckboxes.length > 0 && visibleCheckboxes.every((cb) => cb.checked);
      const parentCheckbox = group.querySelector<HTMLInputElement>(":scope > label input[type='checkbox']");
      if (parentCheckbox) parentCheckbox.checked = allChecked;
      currentItem = group;
    }
  }

  // --- カスケードフィルター実装 ---

  private applyCascadeFilters(): void {
    this.applyCascadeForSites();
    this.applyCascadeForCategories();
    this.applyCascadeForResources();
  }

  private getDateRange(): { start: string; end: string } {
    return { start: this.startDateTarget.value, end: this.endDateTarget.value };
  }

  private isDateInRange(dateStr: string, range: { start: string; end: string }): boolean {
    if (!range.start || !range.end) return true;
    return dateStr >= range.start && dateStr <= range.end;
  }

  private applyCascadeForSites(): void {
    const range = this.getDateRange();
    this.siteItemTargets.forEach((item) => {
      const siteId = item.dataset.siteId;
      if (siteId === "") {
        this.setCascadeHidden(item, false);
        return;
      }
      const dates = (item.dataset.inspectionDates || "").split(",").filter(Boolean);
      const visible = dates.some((d) => this.isDateInRange(d, range));
      this.setCascadeHidden(item, !visible);
    });
    this.updateEmptyMessage(this.noSitesMessageTargets, this.siteItemTargets);
  }

  private applyCascadeForCategories(): void {
    const range = this.getDateRange();
    const selectedSiteContext = this.getSelectedSiteContext();

    this.categoryGroupTargets.forEach((group) => {
      let groupHasVisible = false;

      group.querySelectorAll<HTMLElement>("[data-check-list-id]").forEach((item) => {
        const siteDates = (item.dataset.inspectionSiteDates || "").split(",").filter(Boolean);
        const visible = siteDates.some((pair) => {
          const colonIdx = pair.indexOf(":");
          const siteId = pair.substring(0, colonIdx);
          const dateStr = pair.substring(colonIdx + 1);
          if (!this.isDateInRange(dateStr, range)) return false;
          return this.siteContextMatches(siteId, selectedSiteContext);
        });
        this.setCascadeHidden(item, !visible);
        if (visible) groupHasVisible = true;
      });

      group.querySelectorAll<HTMLElement>("[data-role='category-sub-group']").forEach((subGroup) => {
        const hasVisibleChecklist = Array.from(subGroup.querySelectorAll<HTMLElement>("[data-check-list-id]")).some(
          (cl) => !this.isHidden(cl),
        );
        this.setCascadeHidden(subGroup, !hasVisibleChecklist);
      });

      this.setCascadeHidden(group, !groupHasVisible);
      this.updateGroupParentCheckboxes(group);
    });
    this.updateEmptyMessage(this.noCategoriesMessageTargets, this.categoryGroupTargets);
  }

  private applyCascadeForResources(): void {
    const range = this.getDateRange();
    const selectedSiteContext = this.getSelectedSiteContext();
    const selectedChecklistIds = this.getSelectedChecklistIds();

    this.resourceGroupTargets.forEach((group) => {
      let groupHasVisible = false;

      group.querySelectorAll<HTMLElement>("[data-role='resource-item']").forEach((item) => {
        const pairs = (item.dataset.inspectionPairs || "").split(",").filter(Boolean);
        const visible = pairs.some((triple) => {
          const firstColon = triple.indexOf(":");
          const secondColon = triple.indexOf(":", firstColon + 1);
          const clId = triple.substring(0, firstColon);
          const siteId = triple.substring(firstColon + 1, secondColon);
          const dateStr = triple.substring(secondColon + 1);
          if (!this.isDateInRange(dateStr, range)) return false;
          if (!this.siteContextMatches(siteId, selectedSiteContext)) return false;
          return selectedChecklistIds.includes(clId);
        });
        this.setCascadeHidden(item, !visible);
        if (visible) groupHasVisible = true;
      });

      this.setCascadeHidden(group, !groupHasVisible);
      this.updateGroupParentCheckboxes(group);
    });
    this.updateEmptyMessage(this.noResourcesMessageTargets, this.resourceGroupTargets);
  }

  // --- ヘルパー ---

  private getSelectedSiteContext(): { siteIds: string[]; includeNull: boolean } {
    const siteIds = this.siteCheckboxTargets.filter((cb) => cb.checked && !this.isSearchHidden(cb)).map((cb) => cb.value);
    const includeNull = this.nullSiteCheckboxTargets.some((cb) => cb.checked && !this.isSearchHidden(cb));
    return { siteIds, includeNull };
  }

  private getSelectedChecklistIds(): string[] {
    return this.checklistCheckboxTargets.filter((cb) => cb.checked && !this.isSearchHidden(cb)).map((cb) => cb.value);
  }

  private siteContextMatches(siteId: string, context: { siteIds: string[]; includeNull: boolean }): boolean {
    if (siteId === "") return context.includeNull;
    return context.siteIds.includes(siteId);
  }

  // --- 可視性の状態分離管理 ---

  private setCascadeHidden(item: HTMLElement, hidden: boolean): void {
    if (hidden) {
      item.dataset[CASCADE_HIDDEN] = "true";
      const cb = item.querySelector<HTMLInputElement>("input[type='checkbox']");
      if (cb) cb.checked = false;
    } else {
      const wasHidden = item.dataset[CASCADE_HIDDEN] === "true";
      delete item.dataset[CASCADE_HIDDEN];
      if (wasHidden) {
        const cb = item.querySelector<HTMLInputElement>("input[type='checkbox']");
        if (cb) cb.checked = true;
      }
    }
    this.applyDisplay(item);
  }

  private setSearchHidden(item: HTMLElement, hidden: boolean): void {
    if (hidden) {
      item.dataset[SEARCH_HIDDEN] = "true";
    } else {
      delete item.dataset[SEARCH_HIDDEN];
    }
    this.applyDisplay(item);
  }

  private applyDisplay(item: HTMLElement): void {
    item.style.display = item.dataset[CASCADE_HIDDEN] || item.dataset[SEARCH_HIDDEN] ? "none" : "";
  }

  private isHidden(el: HTMLElement): boolean {
    return el.style.display === "none";
  }

  private isSearchHidden(el: HTMLElement): boolean {
    const item = el.closest<HTMLElement>("[data-search-text]") || el.closest<HTMLElement>("[data-site-id]");
    return item?.dataset[SEARCH_HIDDEN] === "true";
  }

  private updateGroupParentCheckboxes(group: HTMLElement): void {
    const elements = Array.from(group.querySelectorAll<HTMLElement>("[data-child-role]"));
    elements
      .sort((a, b) => this.getDepth(b, group) - this.getDepth(a, group))
      .forEach((g) => {
        const childRole = g.dataset.childRole!;
        const visibleChildren = Array.from(g.querySelectorAll<HTMLElement>(`[data-role='${childRole}']`))
          .filter((el) => !this.isHidden(el))
          .map((el) => el.querySelector<HTMLInputElement>("input[type='checkbox']"))
          .filter((cb): cb is HTMLInputElement => cb !== null);
        const allChecked = visibleChildren.length > 0 && visibleChildren.every((cb) => cb.checked);
        const parentCb = g.querySelector<HTMLInputElement>(":scope > label input[type='checkbox']");
        if (parentCb) parentCb.checked = allChecked;
      });
  }

  private getDepth(element: HTMLElement, root: HTMLElement): number {
    let depth = 0;
    let current = element.parentElement;
    while (current && current !== root) {
      depth += 1;
      current = current.parentElement;
    }
    return depth;
  }

  // --- テキスト検索 ---

  private applySearchFlatList(items: HTMLElement[], text: string): void {
    items.forEach((item) => {
      this.setSearchHidden(item, !this.matchesSearchText(item, text));
    });
  }

  private applySearchHierarchicalList(groups: HTMLElement[], text: string): void {
    groups.forEach((group) => {
      if (!text) {
        group.querySelectorAll<HTMLElement>("[data-search-text]").forEach((el) => {
          this.setSearchHidden(el, false);
        });
        this.setSearchHidden(group, false);
        return;
      }

      const groupMatches = this.matchesSearchText(group, text);
      if (groupMatches) {
        group.querySelectorAll<HTMLElement>("[data-search-text]").forEach((el) => {
          this.setSearchHidden(el, false);
        });
        this.setSearchHidden(group, false);
        return;
      }

      const allItems = Array.from(group.querySelectorAll<HTMLElement>("[data-search-text]"));
      const visibleSet = new Set<HTMLElement>();
      allItems.forEach((el) => {
        if (!this.matchesSearchText(el, text)) return;
        visibleSet.add(el);
        el.querySelectorAll<HTMLElement>("[data-search-text]").forEach((desc) => visibleSet.add(desc));
        let ancestor = el.parentElement?.closest<HTMLElement>("[data-search-text]");
        while (ancestor && group.contains(ancestor)) {
          visibleSet.add(ancestor);
          ancestor = ancestor.parentElement?.closest<HTMLElement>("[data-search-text]") || null;
        }
      });

      allItems.forEach((el) => {
        this.setSearchHidden(el, !visibleSet.has(el));
      });
      this.setSearchHidden(group, visibleSet.size === 0);
    });
  }

  private updateEmptyMessage(messageTargets: HTMLElement[], items: HTMLElement[]): void {
    const anyVisible = items.some((item) => !this.isHidden(item));
    messageTargets.forEach((msg) => msg.classList.toggle("hidden", anyVisible));
  }

  private matchesSearchText(el: HTMLElement, text: string): boolean {
    const searchText = el.dataset.searchText || "";
    return searchText.toLowerCase().includes(text);
  }

  private getInputValue(event: Event): string {
    return ((event.target as HTMLInputElement).value || "").toLowerCase();
  }
}
